import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { CheckCircle, XCircle, Loader2, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

type UnsubscribeStatus = 'loading' | 'success' | 'already_unsubscribed' | 'resubscribed' | 'error';

const Unsubscribe = () => {
  const { token } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const action = searchParams.get('action') || 'unsubscribe';
  
  const [status, setStatus] = useState<UnsubscribeStatus>('loading');
  const [establishmentName, setEstablishmentName] = useState<string>('');
  const [error, setError] = useState<string>('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setError('Lien invalide');
      return;
    }

    processUnsubscribe();
  }, [token, action]);

  const processUnsubscribe = async () => {
    try {
      const { data, error } = await supabase.functions.invoke('unsubscribe-email', {
        body: { token, action }
      });

      if (error) {
        console.error('Unsubscribe error:', error);
        setStatus('error');
        setError('Une erreur est survenue');
        return;
      }

      if (data.establishmentName) {
        setEstablishmentName(data.establishmentName);
      }

      if (action === 'resubscribe') {
        setStatus('resubscribed');
      } else if (data.alreadyUnsubscribed) {
        setStatus('already_unsubscribed');
      } else {
        setStatus('success');
      }
    } catch (err) {
      console.error('Unsubscribe error:', err);
      setStatus('error');
      setError('Une erreur inattendue est survenue');
    }
  };

  const handleResubscribe = async () => {
    setStatus('loading');
    const { error } = await supabase.functions.invoke('unsubscribe-email', {
      body: { token, action: 'resubscribe' }
    });

    if (error) {
      setStatus('error');
      setError('Erreur lors de la réinscription');
    } else {
      setStatus('resubscribed');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/10 via-background to-secondary/10 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">
            {status === 'loading' && (
              <Loader2 className="h-16 w-16 text-primary animate-spin" />
            )}
            {(status === 'success' || status === 'already_unsubscribed') && (
              <CheckCircle className="h-16 w-16 text-green-500" />
            )}
            {status === 'resubscribed' && (
              <Mail className="h-16 w-16 text-primary" />
            )}
            {status === 'error' && (
              <XCircle className="h-16 w-16 text-destructive" />
            )}
          </div>
          
          <CardTitle className="text-2xl">
            {status === 'loading' && 'Traitement en cours...'}
            {status === 'success' && 'Désinscription confirmée'}
            {status === 'already_unsubscribed' && 'Déjà désinscrit'}
            {status === 'resubscribed' && 'Réinscription confirmée'}
            {status === 'error' && 'Erreur'}
          </CardTitle>
          
          <CardDescription className="text-base mt-2">
            {status === 'loading' && 'Veuillez patienter...'}
            {status === 'success' && (
              <>
                Vous ne recevrez plus d'emails marketing
                {establishmentName && <> de <strong>{establishmentName}</strong></>}.
              </>
            )}
            {status === 'already_unsubscribed' && (
              <>
                Vous êtes déjà désinscrit des communications marketing
                {establishmentName && <> de <strong>{establishmentName}</strong></>}.
              </>
            )}
            {status === 'resubscribed' && (
              <>
                Vous êtes maintenant réinscrit aux communications
                {establishmentName && <> de <strong>{establishmentName}</strong></>}.
              </>
            )}
            {status === 'error' && error}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {(status === 'success' || status === 'already_unsubscribed') && (
            <>
              <p className="text-sm text-muted-foreground text-center">
                Note : Vous continuerez à recevoir les emails transactionnels 
                (confirmations de réservation, rappels).
              </p>
              <div className="flex flex-col gap-2">
                <Button variant="outline" onClick={handleResubscribe}>
                  Me réinscrire
                </Button>
                <Button asChild>
                  <Link to="/">Retour à l'accueil</Link>
                </Button>
              </div>
            </>
          )}
          
          {status === 'resubscribed' && (
            <Button asChild className="w-full">
              <Link to="/">Retour à l'accueil</Link>
            </Button>
          )}
          
          {status === 'error' && (
            <Button asChild className="w-full">
              <Link to="/">Retour à l'accueil</Link>
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Unsubscribe;
